import {
  CyclabilityZone,
  CyclabilityZoneService,
  CyclabilityZoneStats,
  Facilities,
  Period,
  TBackendAdministrativeLevel,
  TCyclabilityZonePeriod,
  getCyclabilityZonesPeriods,
} from '@geovelo-frontends/commons';
import { QueryClient } from '@tanstack/react-query';
import moment from 'moment';

export type TZoneStatsMap = { [zoneId: number]: CyclabilityZoneStats[] };

const statsQuery = '{id, administrative_level, stats}';

const defaultMonthsCount = 3;

let defaultPeriod: Period | undefined;

// The same instance is returned all day long, so that every caller builds the same query keys.
export function getDefaultStatsPeriod(): Period {
  const today = moment().startOf('day');

  if (!defaultPeriod || !defaultPeriod.to.isSame(today, 'day')) {
    defaultPeriod = new Period(
      'custom',
      today
        .clone()
        .subtract(defaultMonthsCount - 1, 'months')
        .startOf('month'),
      today,
    );
  }

  return defaultPeriod;
}

const oldestStatsDate = moment('2017-10-01');

export function getOlderStatsPeriod(period: Period, monthsCount: number): Period | null {
  const to = period.from.clone().subtract(1, 'day');
  if (to.isBefore(oldestStatsDate)) return null;

  const from = to
    .clone()
    .subtract(monthsCount - 1, 'months')
    .startOf('month');

  return new Period('custom', moment.max(from, oldestStatsDate), to);
}

export function getMonthsStatsPeriod(monthKey: string, monthsCount: number): Period {
  const month = moment(monthKey);

  return new Period(
    'custom',
    month
      .clone()
      .subtract(monthsCount - 1, 'months')
      .startOf('month'),
    month.clone().endOf('month'),
  );
}

function getRangeKeys(range: TCyclabilityZonePeriod): [string, string] {
  if (range === 'all_time') return ['all_time', 'all_time'];

  return [range.from.format('YYYY-MM-DD'), range.to.format('YYYY-MM-DD')];
}

export const statsKeys = {
  zone: (zoneId: number, administrativeLevel: string, range: TCyclabilityZonePeriod) =>
    ['cyclability-zones', zoneId, administrativeLevel, ...getRangeKeys(range)] as const,
  children: (zoneId: number, administrativeLevel: string, range: TCyclabilityZonePeriod) =>
    ['cyclability-zones', zoneId, administrativeLevel, ...getRangeKeys(range)] as const,
};

export function toStatsMap(zones: CyclabilityZone[]): TZoneStatsMap {
  return zones.reduce<TZoneStatsMap>((res, { id, stats }) => {
    res[id] = stats;

    return res;
  }, {});
}

export function mergeStats(parts: CyclabilityZoneStats[][]): CyclabilityZoneStats[] {
  const statsByKey = new Map<string, CyclabilityZoneStats>();
  parts.forEach((stats) => stats.forEach((stat) => statsByKey.set(stat.dateAsKey, stat)));

  return [...statsByKey.values()].sort((a, b) => a.dateAsKey.localeCompare(b.dateAsKey));
}

const distanceKeys: (Facilities | 'all')[] = [
  Facilities.Cycleways,
  Facilities.Greenways,
  Facilities.Lanes,
  Facilities.MixedFacilities,
  Facilities.Opposites,
  Facilities.SharedBusways,
  'all',
];

export function sumStats(statsMap: TZoneStatsMap): CyclabilityZoneStats[] {
  const zones = Object.entries(statsMap).map(([id, stats]) => ({ id: Number(id), stats }));

  return getCyclabilityZonesPeriods(zones).map(({ key, date }) => {
    const distances = distanceKeys.reduce(
      (res, distanceKey) => ({ ...res, [distanceKey]: 0 }),
      {} as CyclabilityZoneStats['distances'],
    );

    zones.forEach(({ stats }) => {
      const periodStats = stats.find(({ dateAsKey }) => dateAsKey === key);
      if (periodStats)
        distanceKeys.forEach((distanceKey) => {
          distances[distanceKey] += periodStats.distances[distanceKey];
        });
    });

    return new CyclabilityZoneStats(date, distances);
  });
}

export async function fetchZoneStats(
  zone: CyclabilityZone,
  period: TCyclabilityZonePeriod,
): Promise<CyclabilityZoneStats[]> {
  const { stats } = await CyclabilityZoneService.getZone(zone.id, { period, query: statsQuery });

  return stats;
}

export async function fetchChildrenStats(
  zone: CyclabilityZone,
  administrativeLevel: TBackendAdministrativeLevel,
  period: TCyclabilityZonePeriod,
): Promise<TZoneStatsMap> {
  if (zone.administrativeLevel === 'world') {
    const { zones } = await CyclabilityZoneService.getZones({
      administrativeLevel,
      rowsPerPage: 100,
      period,
      query: statsQuery,
    });

    return toStatsMap(zones);
  }

  return toStatsMap(
    await CyclabilityZoneService.getChildrenZones(zone.id, {
      administrativeLevel,
      period,
      query: statsQuery,
    }),
  );
}

export async function fetchFRDepartments(period: Period): Promise<CyclabilityZone[]> {
  const { zones } = await CyclabilityZoneService.getZones({
    administrativeLevel: 'DEPARTMENT',
    countryCode: 'fr',
    rowsPerPage: 100,
    period,
    query: '{id, administrative_level, name, stats}',
  });

  return zones;
}

// The zone map only holds the identity and the geometry of a zone: stats depend on the period
// and are kept in the query cache.
export function cacheZonesStats(
  queryClient: QueryClient,
  zones: CyclabilityZone[],
  period: TCyclabilityZonePeriod,
) {
  zones.forEach((zone) => {
    queryClient.setQueryData(statsKeys.zone(zone.id, zone.administrativeLevel, period), zone.stats);
    zone.stats = [];
    zone.statsBNSC = [];
  });
}

export function cacheChildrenStats(
  queryClient: QueryClient,
  parent: CyclabilityZone,
  administrativeLevel: string,
  children: CyclabilityZone[],
  period: TCyclabilityZonePeriod,
) {
  queryClient.setQueryData(
    statsKeys.children(parent.id, administrativeLevel, period),
    toStatsMap(children),
  );
  cacheZonesStats(queryClient, children, period);
}

export function withStats(zone: CyclabilityZone, stats: CyclabilityZoneStats[]): CyclabilityZone {
  return Object.assign(Object.create(Object.getPrototypeOf(zone)), zone, { stats });
}
